<script lang="ts">
  import type { PlayerInterface } from '@foundation-player/loader';
  import { getContext } from 'svelte';
  import AdIndicator from '../../shared/components/AdIndicator.svelte';
  import Fullscreen from './Fullscreen.svelte';
  import PlayPause from '../../shared/components/PlayPause.svelte';
  import ReadonlyProgress from '../../shared/components/ReadonlyProgress.svelte';
  import Volume from '../../shared/components/Volume.svelte';
  import type { PlayerStateContext } from '../../shared/types/player-state';
  import AdLink from '../../shared/components/AdLink.svelte';

  export let player: PlayerInterface;
  export let fullscreenElement: HTMLElement;

  const { adClip, adDuration, currentAdTime, playing } = getContext<PlayerStateContext>('playerState');

  const togglePlay = () => {
    if (player.isPlaying()) {
      player.pause();
    } else {
      player.play();
    }
  };
</script>

<!-- svelte-ignore a11y-no-static-element-interactions a11y-click-events-have-key-events -->
<div class="advertisement-layer" on:click|self={togglePlay}>
  {#if $adClip?.hasClickThrough}
    <AdLink {player} />
  {/if}

  {#if !$playing}
    <div class="pause">
      <div class="pause-button">
        <!-- This is just a visual duplication of the pause button in the controls bar. So we hide it from screen readers. -->
        <PlayPause
          {player}
          size="xl"
          aria-hidden="true"
          style="pointer-events: initial"
          focusLayer="advertising"
          focusName="playPausePrime"
        />
      </div>
    </div>
  {/if}

  <div class="controls">
    <PlayPause {player} focusLayer="advertising" focusName="playPause" />
    <div class="ad-indicator-wrapper">
      <AdIndicator />
    </div>

    <span class="spacer"></span>

    <Volume {player} mode="only-mute-unmute" focusLayer="advertising" focusName="volume" />
    <Fullscreen {player} {fullscreenElement} focusLayer="advertising" focusName="fullscreen" />
  </div>

  <ReadonlyProgress aria-label="Zeit im Werbeblock" currentTime={$currentAdTime} duration={$adDuration} />
</div>

<style module>
  .advertisement-layer {
    height: 100%;

    display: flex;
    flex-direction: column;

    justify-content: flex-end;
    justify-content: end;

    --slider-track-color: var(--advertisement-color);
  }

  .pause {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .controls {
    display: flex;
    align-items: center;

    padding: 4px;
    padding: clamp(0px, 1cqw - 4px, 4px);
  }

  /* add drop shadow to icons */
  .controls :global(svg) {
    filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.75));
  }

  .spacer {
    flex: 1;
    pointer-events: none;
  }

  .ad-indicator-wrapper {
    padding-left: 4px;
  }

  @media (prefers-contrast: more) {
    .controls,
    .pause-button {
      background: var(--controls-contrast-background);
    }

    .controls :global(svg) {
      /* Since we set a background to increase the contrast, we don't need the
       * drop shadow anymore. */
      filter: none;
    }
  }

  @media (prefers-contrast: more) and (prefers-reduced-transparency: reduce) {
    .controls,
    .pause-button {
      background: var(--controls-no-transparency-background);
    }
  }
</style>
